import { SelectionModel } from '@angular/cdk/collections';
import {
    AfterViewInit,
    booleanAttribute,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    inject,
    Input,
    isDevMode,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TrackByFunction,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import {
    MatLegacyTable as MatTable,
    MatLegacyTableDataSource as MatTableDataSource,
} from '@angular/material/legacy-table';
import { Router } from '@angular/router';
import { IAppRequest } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { AppHelper } from '@app/core/app.helper';
import { TokenStorage } from '@app/core/token.storage';
import { TableColumnActionComponent } from '@app/modules/table/children/table-column-action/components/table-column-action.component';
import {
    EActionColumn,
    TActionColumn,
} from '@app/modules/table/children/table-column-action/interfaces/table-column-action.enum';
import {
    IFilterRun,
    IGroupRun,
    IHideRun,
    IPinOrUnpinRun,
    IRenameDataSave,
    ISortRun,
    IValueForm,
} from '@app/modules/table/children/table-column-action/interfaces/table-column-action.interface';
import {
    TCustomSortDirection,
    TGroupCustomAction,
} from '@app/modules/table/children/table-column-action/interfaces/table-column-action.type';
import { TableColumnActionService } from '@app/modules/table/children/table-column-action/table-column-action.service';
import {
    IDataColumnMenu,
    IPaletteColumnData,
} from '@app/modules/table/children/table-column-header-menu/table-column-header-menu.interface';
import { TableHeaderComponent } from '@app/modules/table/children/table-header/components/table-header.component';
import { TABLE_FIXED_COLUMS } from '@app/modules/table/constants/table.constants';
import { TableEventService } from '@app/modules/table/services/table-event.service';
import { TableReorderButtonsService } from '@app/modules/table/services/table-reorder-buttons.service';
import { ITableContextMenuRow, ITableRowContextMenuEvent } from '@app/modules/table/table-context-menu.interface';
import {
    createTableContextMenuRecord,
    createTableRowContextMenuEvent,
} from '@app/modules/table/table-context-menu.lib';
import { TableService } from '@app/modules/table/table.service';
import { IOutputEventData } from '@components/buttons-panel/buttons-panel.interface';
import { AppDialogService } from '@components/dialog/services/app-dialog.service';
import { stripHtml } from '@fui/fui-helpers';
import { IPaletteRecord } from '@modules/palettes/interfaces/palettes.interface';
import { FormaterPipe } from '@pipes/formater/formater.pipe';
import { FuiDestroyService } from '@services/destroy.service';
import { NotifierService } from '@services/notifier.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, defer } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { getSpinnerHtml } from 'src/shared/helpers/spinner-helpers';
import { IFactorHistorySidebarData } from '../factor-row/interfaces/factor-row.interface';
import { FactorRowHistoryService } from '../factor-row/services/factor-row-history.service';
import { FactorRowService } from '../factor-row/services/factor-row.service';
import { TableCellsService } from './children/table-cells/table-cells.service';
import { TreeNode } from './dialogs/settings-table/interfaces/settings.table.dialog.interface';
import { SettingsTableDialogComponent } from './dialogs/settings-table/settings.table.dialog.component';
import { DEFAULT_PAGE_SIZE } from './pagination/custom.pagination.component';
import { TableSkeletonService } from './table-skeleton/table-skeleton.service';
import {
    IButton,
    InterfaceSettingsTable,
    InterfaceSettingsUserColumns,
    InterfaceTableElement,
    ITableCustomFilter,
} from './table.interface';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./styles/table.component.scss', './styles/table.component.adaptive.scss'],
    providers: [
        TableSkeletonService,
        FuiDestroyService,
        FormaterPipe,
        TableEventService,
        TableReorderButtonsService,
        TableCellsService,
        FuiDestroyService,
        TableColumnActionService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    private readonly formBuilder = inject(FormBuilder);
    /** Признак полноэкранного режима */
    isFullScreenMode = false;
    /** Элемент пагинации */
    @ViewChild('UsersMatPaginatorTop', { static: true }) paginatorTables;
    /** Элемент сортировки */
    @ViewChild('UsersMatSort', { static: true }) sortTables;
    @ViewChild('dataMatTable', { static: false }) dataMatTable: MatTable<any>;
    @ViewChild('globalSearchInput', { static: true })
    globalSearchInput: FormControl = new FormControl();
    @ViewChild('tableContainer', { static: false }) tableContainer: ElementRef;
    @ViewChild('tableCard', { static: false, read: ElementRef }) tableCard;
    /** Заголовок таблицы */
    @ViewChild('tableHeader', { static: false, read: ElementRef }) tableHeader: ElementRef;
    @ViewChild('tableHeader', { static: false, read: TableHeaderComponent }) tableHeaderComponent: TableHeaderComponent;
    @ViewChild('matTable', { static: false }) matTable: ElementRef;
    /** Элемент панели таблицы (кнопки, пагинация) */
    @ViewChild('tablePanel')
    tablePanel: ElementRef;

    /** Кнопки действий над столбцом */
    @ViewChild('groupButton', { read: ElementRef }) groupButtonRef!: ElementRef<HTMLElement>;
    @ViewChild('summarizeButton', { read: ElementRef }) summarizeButtonRef!: ElementRef<HTMLElement>;
    @ViewChild('sortButton', { read: ElementRef }) sortButtonRef!: ElementRef<HTMLElement>;
    @ViewChild('filterButton', { read: ElementRef }) filterButtonRef!: ElementRef<HTMLElement>;

    /** Компонент действий над столбцом (Popup) */
    @ViewChild('tableColumnAction', { static: false, read: TableColumnActionComponent })
    tableColumnAction!: TableColumnActionComponent;

    /**  Добавочные фильтры запроса */
    @Input() customFilter: ITableCustomFilter = {};
    /** Динамическая часть фильтра */
    @Input() dynamicFilter: any = null;
    /** Имя полей */
    @Input() dynamicFieldName: any = null;
    /** Значения расширенного поиска */
    @Input() extendFilter: any = {};

    /** Локальные данные со стабильными ID; состояние групп сохраняется между обновлениями. */
    @Input() hasLocalData = false;

    /** Поле с уникальным стабильным значением для сопоставления строк; без него используется ссылка на объект. */
    @Input() trackByColumn?: string;

    /** Готовые номера видимых строк с учётом страницы; у заголовков групп номер отсутствует. */
    rowNumbers: (number | undefined)[] = [];

    /** Разрешить группировку в локальной таблице, помимо таблиц реестров. */
    @Input() allowGrouping = false;

    /**
     * Вписывать колонки в ширину контейнера без ручного растягивания.
     * Вертикальную прокрутку содержимого выполняет родительская область.
     */
    @Input() @HostBinding('class.fit-columns-to-container') fitColumnsToContainer = false;

    /** Режим бесконечной прокрутки вместо пагинации; добавлением порций управляет родитель. */
    @Input() infiniteScroll = false;

    /** Подготовка следующей порции: показать скелетон под уже загруженными строками. */
    @Input() isLoadingMore = false;

    /** Доступны ли общие действия группировки для текущей таблицы. */
    get canGroupColumns(): boolean {
        return this.allowGrouping || this.tableService?.constructor.name === 'FactorInputService';
    }

    /** История загружается при открытии: её содержимое само переиспользует модуль таблицы. */
    readonly historySidebarComponent$ = defer(() =>
        import('../factor-row/component/factor-history-sidebar/factor-history-sidebar.component').then(
            (module) => module.FactorHistorySidebarComponent,
        ),
    );

    /** Измененный список колонок */
    @Input() editColumns = [];
    /**  Сервис взаимодействия страницы */
    @Input() tableService: TableService | any;

    /** Набор кнопок */
    @Input() buttons: {
        global?: IButton[];
        globalMenu?: IButton[];
        local?: IButton[];
    } = { global: [], globalMenu: [], local: [] };

    /** Добавление кнопок от настроек таблицы */
    @Input() addButtonsInTable: {
        [key: string]: {
            button: IButton;
            menu: string[];
        };
    } = {};

    @Input() showElement: boolean = false;
    //TODO: все output которые обрабатывают кнопки нужно заменить на buttonClick
    @Output() reassign = new EventEmitter<any>();
    @Output() saveCard = new EventEmitter<any>();
    @Output() add = new EventEmitter<any>();
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() copy = new EventEmitter<any>();
    @Output() edit = new EventEmitter<any>();
    @Output() customDeleteArray = new EventEmitter<any>();
    @Output() send = new EventEmitter<any>();
    @Output() report = new EventEmitter<any>();
    @Output() createPrivateOffice = new EventEmitter<any>();
    @Output() changeSubject = new EventEmitter<any>();
    @Output() deleteForPeriod = new EventEmitter<any>();
    @Output() activated = new EventEmitter<any>();
    @Output() deactivated = new EventEmitter<any>();
    @Output() openDialog = new EventEmitter<any>();
    @Output() dblClickRow = new EventEmitter<any>();
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() show = new EventEmitter<any>();
    @Output() export = new EventEmitter<any>();
    @Output() import = new EventEmitter<void>();
    @Output() calculatePricing = new EventEmitter<any>();
    @Output() createGeocodeGeometry = new EventEmitter<any>();
    @Output() makeCalculation = new EventEmitter<any>();
    @Output() onFieldChange = new EventEmitter<any>();
    @Output() searchDuplicates = new EventEmitter<any>();
    @Output() generateReport = new EventEmitter<any>();
    @Output() copyAndCreate = new EventEmitter<any>();
    @Output() acceptTheReport = new EventEmitter<any>();
    @Output() rejectTheReport = new EventEmitter<any>();
    @Output() viewSection = new EventEmitter<any>();
    @Output() readArray = new EventEmitter<any>();
    @Output() exportPdf = new EventEmitter<any>();
    @Output() showReport = new EventEmitter<any>();
    @Output() addOnService = new EventEmitter<void>();
    @Output() showFile = new EventEmitter<any>();
    @Output() moveMessage = new EventEmitter<any>();
    @Output() restoreMessage = new EventEmitter<any>();
    @Output() reply = new EventEmitter<any>();
    @Output() setMapLink = new EventEmitter<any>();
    @Output() linkMap = new EventEmitter<any>();
    @Output() getChangeTracking = new EventEmitter<any>();
    @Output() generateCharges = new EventEmitter<any>();
    @Output() exportChargePaymentReport = new EventEmitter<any>();
    @Output() deleteGeometry = new EventEmitter<any>();
    /* Открыть окно настроек фильтра для регламентированного очета */
    @Output() getReportFilterSettings = new EventEmitter<any>();
    @Output() addLayer = new EventEmitter<any>();
    /** Открытие диалогового окна настройки команды ОКС */
    @Output() editTeamList = new EventEmitter<any>();
    /** Переопределяем удаление в таблице */
    @Output() customDelete = new EventEmitter<any>();
    /** Отправка данных по простым нажатиям кнопки */
    @Output() buttonClick = new EventEmitter<IOutputEventData<string>>();

    /**
     * ПКМ по строке, если нет шапки с PopupContent (меню рисует оболочка видов)
     */
    @Output() rowContextMenu = new EventEmitter<ITableRowContextMenuEvent>();

    /**
     * Событие, выбрасываемое при старте выполнения метода getData
     */
    @Output() dataRequested = new EventEmitter<void>();

    /**
     * Число найденных записей после загрузки (для панели поиска на странице реестра)
     */
    @Output() recordsFoundChange = new EventEmitter<number>();

    /**
     * Событие, выбрасываемое по завершению выполнения метода getData
     * Возвращает новые данные, полученные таблицей
     */
    @Output() dataReceived = new EventEmitter<any[]>();

    /** Число видимых строк изменилось после сворачивания или раскрытия групп. */
    @Output() rowsVisibilityChanged = new EventEmitter<void>();

    /** Настройки дерева */
    @Input() groupByTreeColumns: {
        columnParent: string;
        columnGroup: string;
        isTree: boolean;
        rootId?: string | number;
    } = {
        columnParent: 'parent_id',
        columnGroup: null,
        isTree: false,
        rootId: 1,
    };
    /** Настройка редактирования строк */
    @Input() settingsEditFields: {
        isEdit: boolean;
        excludeField: string;
        fields: { [key: string]: any };
    } = {
        isEdit: false,
        excludeField: null,
        fields: {},
    };

    /** Полные данные записей */
    filteredDataTreeAll: any[] = [];

    classTable: string = 'table-container-random-' + (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    loadSettings: boolean = false;
    /** Данные цветов по статусам */
    dataPalette: IPaletteRecord[] = null;
    formEdit: FormGroup = this.formBuilder?.group({});
    /** Показать панель истории изменений поля связи. */
    showHistorySidebar$ = new BehaviorSubject<boolean>(false);
    /** Данные истории изменений поля связи. */
    readonly historySidebarData$ = new BehaviorSubject<IFactorHistorySidebarData | null>(null);
    /**
     * Ошибки на странице
     * @type {string}
     */
    requestError = '';
    /**
     * Элемент таблицы
     * @type {MatTableDataSource<>}
     */
    dataTables = new MatTableDataSource();

    /** Флаг группировки таблицы (нужен из-за специфичной группировки в субьектах автоматизации) */
    isGroup: boolean = false;

    /** Имя для спиннера компонента */
    spinnerName = '';

    selection = new SelectionModel(true, []);
    /**
     * Число записей таблицы
     * @type {number}
     */
    lengthTables = 0;
    /** Ширина колонки по умолчанию */
    defaultWidth: number = 80;
    /** Минимальная ширина колонки */
    minColumnWidth: number = 45;
    /**
     * Набор количества строк для таблицы
     * @type {number[]}
     */
    pageSizeOptionsTables = [20, 50, 100, 200];
    /**  Событие выбора количества записей на странице */
    pageEvent: PageEvent;
    /** Выбор палитры по которой будут краситься строки */
    mainPalette: string = 'status_date';
    readonly #cdr = inject(ChangeDetectorRef);
    readonly tableReorderButtonsService = inject(TableReorderButtonsService);
    /** Сервис действий над столбцами таблицы */
    readonly #tableColumnActionService = inject(TableColumnActionService);
    readonly tableEventService = inject(TableEventService);
    private readonly tableCellsService = inject(TableCellsService);
    private readonly destroy$ = inject(FuiDestroyService);

    /** Ожидающие таймеры обновления данных после изменения настроек таблицы. */
    private readonly settingsReloadTimeouts = new Set<ReturnType<typeof setTimeout>>();

    /** Метод для очистки тегов */
    protected readonly stripHtml = stripHtml;
    /** Массив кнопок таблицы */
    globalButtons$: BehaviorSubject<IButton[]> = new BehaviorSubject<IButton[]>([]);
    /** Массив кнопок глобального меню таблицы */
    globalMenuButtons$: BehaviorSubject<IButton[]> = new BehaviorSubject<IButton[]>([]);
    /** Событие нажатие клавиши shift */
    shiftKeyPressed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     * Флаг пустого результата поиска
     */
    emptyResult$ = new BehaviorSubject<boolean>(true);
    /** Показывать/скрывать основной контент таблицы */
    showTableContent$ = new BehaviorSubject<boolean>(true);
    /** Фильтры таблицы */
    filterTable = {};
    /** Настройки таблицы */
    settingsTable: InterfaceSettingsTable = {
        displayedColumns: ['select'],
    };

    /**
     * Столбцы индикаторов (палитры), по которым можно выполнять действия
     */
    showMenuColumnPalette$: BehaviorSubject<Record<string, IPaletteColumnData>> = new BehaviorSubject<
        Record<string, IPaletteColumnData>
    >({});

    /** Название действия над колонкой (компонент TableColumnActionComponent) */
    columnActionType: TActionColumn;

    /** Задание строки подсказки в заголовке компонента TableColumnActionComponent */
    columnActionTooltip = new BehaviorSubject<null | { preloader: boolean; text: string }>(null);

    /** Значения действия над столбцом */
    valuesFormColumnActions: IValueForm[] = [];

    /**
     * Текущая таблица
     */
    @Input() item;
    /**
     * Текущий язык
     */
    @Input() currentLangRelationForm;
    /**
     * Показать все изменения
     */
    @Input() isChangeTracking = false;
    /**
     * Наименование модуля таблицы для сохранения пользовательских настроек
     */
    @Input() tableModule: string;
    /**
     * Показывать шапку таблицы (`fui-table-header`: слоты, кнопки, кебаб).
     * В шапке же PopupContent для ПКМ по строке — при `false` меню не монтируется,
     * ПКМ нужно открывать снаружи (например оболочка видов реестра).
     */
    @Input() showTableHeader: boolean = true;

    /**
     * Таблица на всю доступную высоту контейнера.
     * На странице реестра высоту при открытом поиске меняет `IRegistryActiveView.setFullHeight`.
     */
    readonly #isFullHeight$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    dynamicSystemFields: string[] = ['status_name'];
    /** Глобальный поиск */
    globalFilter = '';

    /** Флаг загрузки данных для отрисовки */
    public isLoaded$ = new BehaviorSubject<boolean>(false);

    /** Спиннер */
    readonly spinnerHtml = getSpinnerHtml();

    /**
     * Сопоставить строки по настроенному полю.
     * @param _index Индекс строки в отображаемом наборе
     * @param row Значения колонок строки
     */
    readonly rowTrackBy: TrackByFunction<InterfaceTableElement> = (_index, row) =>
        this.trackByColumn ? row[this.trackByColumn] : row;

    public readonly trackByUserColumnsName = (column) => column.title;

    /**
     * Занять всю высоту (экран списка) или оставить intrinsic (диалоги / вложенные таблицы)
     * @param val true — full-height классы
     */
    @Input({ transform: booleanAttribute })
    set isFullHeight(val: boolean) {
        this.#isFullHeight$.next(val);
    }

    get isFullHeight(): boolean {
        return this.#isFullHeight$.value;
    }

    public get isFullHeight$() {
        return this.#isFullHeight$.asObservable();
    }

    constructor(
        public router: Router,
        public notifier: NotifierService,
        public dialog: MatDialog,
        public spinner: NgxSpinnerService,
        public appService: AppService,
        private dialogConfirm: AppDialogService,
        private token: TokenStorage,
        public factorRowHistoryService: FactorRowHistoryService,
        public factorRowService: FactorRowService,
        public tableSkeleton: TableSkeletonService,
    ) {
        this.spinnerName = AppHelper.randomInteger(1, 999999).toString();
        this.tableCellsService.getSettingsTableFn = () => this.settingsTable;
    }

    /** Показать историю изменений поля связи. */
    showRevisionHistory(tableData: unknown): void {
        if (this.showHistorySidebar$.value) {
            this.showHistorySidebar$.next(false);
            // Выполняем уничтожение прежнего содержимого до повторного открытия в этом же событии.
            this.#cdr.detectChanges();
        }
        this.historySidebarData$.next(
            this.factorRowHistoryService.showRevisionHistory(
                { id: this.factorRowService.factorId, data_type: 'relation' },
                {
                    factorDataId: this.factorRowService.factorDataId,
                    language: this.currentLangRelationForm.lang,
                    tableData,
                },
            ),
        );
        this.showHistorySidebar$.next(true);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.item || changes.currentLangRelationForm || changes.tableService) {
            this.showHistorySidebar$.next(false);
            this.historySidebarData$.next(null);
        }
        if (this.editColumns.length > 0) {
            this.settingsTable.displayedColumns = this.editColumns;
        }
        for (const propName in changes) {
            if (changes.hasOwnProperty(propName)) {
                if (propName === 'tableService' && changes[propName].previousValue !== undefined) {
                    this.ngAfterViewInit();
                }
                if (propName === 'buttons') {
                    this.adjustButtons();
                }
            }
        }
    }

    /**
     * Вызов родительской функции
     * @param type тип
     * @param value значение
     * @param event событие нажатия
     */
    callParent(type, value, event) {
        this.appService.openNewTab = event.ctrlKey || event.metaKey;
        switch (type) {
            case 'add':
                this.add.next(value);
                break;
            case 'send':
                this.send.next(value);
                break;
            case 'report':
                this.report.next(value);
                break;
            case 'edit':
                this.saveScrollTable();
                this.edit.next(value);
                break;
            case 'deleteArray':
                this.removeRecords(value);
                break;
            case 'calculatePricing':
                this.calculatePricing.next(value);
                break;
            case 'createGeocodeGeometry':
                this.createGeocodeGeometry.next(value);
                break;
            case 'makeCalculation':
                this.makeCalculation.next(value);
                break;
            case 'changeSubject':
                this.changeSubject.next(value);
                break;
            case 'deleteForPeriod':
                this.deleteForPeriod.next(value);
                break;
            case 'activated':
                this.activated.next(value);
                break;
            case 'deactivated':
                this.deactivated.next(value);
                break;
            case 'customDeleteArray':
                this.customDeleteArray.next(value);
                break;
            case 'openDialog':
                this.openDialog.next(value);
                break;
            case 'searchDuplicates':
                this.searchDuplicates.next(value);
                break;
            case 'generateReport':
                const settings = {
                    rows: this.settingsTable.userSize,
                    page: this.settingsTable.userIndex,
                    sort: this.getSortParam(this.settingsTable.userSort),
                    global_filter: this.globalFilter,
                    custom_filter: this.customFilter,
                    extended_filter: this.extendFilter,
                    filter: this.settingsTable.userFilter,
                    settings: this.settingsTable,
                    value,
                };
                this.generateReport.next(settings);
                break;
            case 'dblClickRow':
                this.saveScrollTable();
                this.dblClickRow.next(value);
                break;
            case 'show':
                this.saveScrollTable();
                this.show.next(value);
                break;
            case 'createPrivateOffice':
                this.createPrivateOffice.next(value);
                break;
            case 'export':
                const data = {
                    rows: this.settingsTable.userSize,
                    page: this.settingsTable.userIndex,
                    sort: this.getSortParam(this.settingsTable.userSort),
                    global_filter: this.globalFilter,
                    custom_filter: this.customFilter,
                    extended_filter: this.extendFilter,
                    filter: this.settingsTable.userFilter,
                    settings: this.settingsTable,
                };
                this.export.next(data);
                break;
            case 'import':
                this.import.next();
                break;
            case 'addOnService':
                this.addOnService.next();
                break;
            case 'showFile':
                this.showFile.next(value);
                break;
            case 'copyAndCreate':
                this.copyAndCreate.next(value);
                break;
            case 'acceptTheReport':
                this.acceptTheReport.next(value);
                break;
            case 'rejectTheReport':
                this.rejectTheReport.next(value);
                break;
            case 'viewSection':
                this.viewSection.next(value);
                break;
            case 'readArray':
                this.readArray.next(value);
                break;
            case 'exportPdf':
                this.exportPdf.next(value);
                break;
            case 'showReport':
                this.showReport.next(value);
                break;
            case 'moveMessage':
                this.moveMessage.next(value);
                this.getData(false);
                break;
            case 'restoreMessage':
                this.restoreMessage.next(value);
                this.getData(false);
                break;
            case 'reply':
                this.reply.next(value);
                break;
            case 'setMapLink':
                this.setMapLink.next(value);
                break;
            case 'linkMap':
                this.linkMap.next(value);
                break;
            case 'addLayer':
                this.addLayer.next(value);
                break;
            case 'getChangeTracking':
                this.getChangeTracking.next(value);
                break;
            case 'generateCharges':
                this.generateCharges.next(value);
                break;
            case 'exportChargePaymentReport':
                this.exportChargePaymentReport.next(value);
                break;
            case 'deleteGeometry':
                this.deleteGeometry.next(value);
                break;
            case 'getReportFilterSettings':
                this.getReportFilterSettings.next(value);
                break;
            case 'reassign':
                this.reassign.next(value);
                break;
            case 'editTeamList':
                this.editTeamList.next(value);
                break;
            case 'customDelete':
                this.customDelete.next(value);
                break;
            case 'copy':
                this.copy.next(value);
                break;
            case 'fullScreenMode':
                if (this.isFullScreenMode) {
                    this.#turnFullScreenOff();
                } else {
                    // Полноэкранные режим
                    this.isFullScreenMode = true;
                    this.appService.showSideMenu.next(false);
                    this.notifier.notify('success', this.appService.language_dictionary.full_screen_mode_on);
                    this.adjustButtons();
                }
                this.broadcastButtonChanges();
                break;
            default:
                this.buttonClick.next({ name: type, data: value });
        }
    }

    /**
     * Сохранение скролла таблицы
     */
    saveScrollTable(clear: boolean = false) {
        let scroll = 0;
        if (!clear && document.querySelector('.mat-table-container')) {
            scroll = document.querySelector('.mat-table-container').scrollTop;
        }
        this.token.saveScrollTable(this.getTableKey('ScrollTable'), scroll);
    }

    /**
     * Событие окончания инициализации компонента
     */
    ngOnInit() {
        if (this.tableService) {
            this.tableSkeleton.show();
        }
        // Подписываемся на изменение имени колонки
        this.#tableColumnActionService.eventNewColumnName$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: IRenameDataSave) => {
                this.#renameColumn(data);
            });
    }

    /** Изменение названия колонки таблицы в настройках пользователя */
    #renameColumn(data: IRenameDataSave) {
        // Сохраняем измененное название
        this.settingsTable.userColumns.map((item) => {
            if (item.name === data.name) {
                item.title = data.title;
            }
        });
        this.settingsTable.userTree.select.map((item) => {
            if (item.alias === data.name) {
                item.name = data.title;
            }
        });
        // Сохраняем изменения в бд
        this.#afterChangingSettings(false);
    }

    /**
     * Разрушение компонента
     */
    ngOnDestroy() {
        this.settingsReloadTimeouts.forEach((timeout) => clearTimeout(timeout));
        this.settingsReloadTimeouts.clear();

        this.buttonClick.complete();
        if (this.isFullScreenMode) {
            this.appService.showSideMenu.next(true);
        }
    }

    /**
     * Событие после завершения инициализации вьюшки
     */
    ngAfterViewInit(): void {
        this.broadcastButtonChanges();
        this.loadSettings = false;
        let settingData = Object.assign({}, this.customFilter);
        if (this.dynamicFilter) {
            settingData = Object.assign(settingData, this.dynamicFilter);
        }
        if (this.tableModule) {
            settingData.module = this.tableModule;
        }
        this.spinner.show(this.spinnerName);

        this.tableService
            .getTableSettings(settingData)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (settingsData: IAppRequest) => {
                    if (!settingsData || settingsData.is_error === true) {
                        throw new Error(settingsData.message);
                    }

                    this.settingsTable = Object.assign(this.settingsTable, settingsData.data);

                    // Если пришли настройки группировки, значит отображаем таблицу в древовидном виде
                    if (this.settingsTable.userGroup && this.settingsTable.userGroup.length > 0) {
                        this.groupByTreeColumns.isTree = true;
                        this.isGroup = true;
                    }

                    /* Добавление кнопок в меню таблицы (во все 3) */
                    this.adjustButtons();

                    if (!this.settingsTable.displayedColumns) {
                        this.settingsTable.displayedColumns = [];
                    }

                    if (!this.settingsTable.userColumns) {
                        this.setFirstConfig();
                    } else {
                        this.setAllColumns();
                    }
                    this.normalizeUserSort();
                    this.clearDisplayedColumns();

                    /* Установка данных для палитры */
                    this.setPaletteData(settingsData.data.palette);
                    this.settingsTable.displayedColumns = this.#sortDisplayedColumns(
                        this.settingsTable.displayedColumns,
                    );

                    this.setCellForColumns();
                    this.dataTables.paginator = this.paginatorTables;
                    this.dataTables.sort = this.sortTables;
                    this.getData(this.tableService.isSearch);
                    this.broadcastButtonChanges();
                    this.loadSettings = true;
                },
                error: (error: unknown) => {
                    console.error(error);

                    this.spinner.hide(this.spinnerName);
                    this.isLoaded$.next(true);
                    this.showTableHeader = false;
                    this.requestError = this.appService.language_dictionary.system_no_connection_with_system;
                    this.tableSkeleton.hide();
                    this.#cdr.detectChanges();
                },
            });

        // Подписка на изменение выделения строк
        this.selection.changed.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.buttons = this.tableReorderButtonsService.reorderButtons(this.buttons, this.selection.selected.length);
            this.broadcastButtonChanges();
        });
        this.selectionKeyboardHandler();
    }

    /**
     * При инициализации и каждый раз, когда список кнопок задаётся извне,
     * нам нужно дополнить его дополнительными кнопками из addButtonsInTable
     * согласно настройкам доступности кнопок из settingsTable
     */
    private adjustButtons() {
        const buttonList = Object.keys(this.addButtonsInTable);
        if (buttonList?.length) {
            buttonList.forEach((keyButton) => {
                if (this.settingsTable?.buttons && this.settingsTable?.buttons[keyButton]) {
                    const buttonToAdd = this.addButtonsInTable[keyButton];
                    if (buttonToAdd.button.click === 'fullScreenMode') {
                        if (this.isFullScreenMode) {
                            buttonToAdd.button.color = 'soft';
                        } else {
                            buttonToAdd.button.color = 'flat';
                        }
                    }
                    buttonToAdd.menu.forEach((menuButton) => {
                        if (this.buttons[menuButton].findIndex((x) => x.name === buttonToAdd.button.name) === -1) {
                            this.buttons[menuButton].push(buttonToAdd.button);
                        }
                    });
                }
            });
        }

        [this.buttons.globalMenu].forEach((arr) => {
            arr.sort((a, b) => {
                const isDelA = a?.icon.indexOf('delete');
                const isDelB = b?.icon.indexOf('delete');
                return isDelA >= isDelB ? 1 : -1;
            });
        });

        this.broadcastButtonChanges();
    }

    /**
     * Оповестить слушателей об изменении текущего набора кнопок
     */
    private broadcastButtonChanges() {
        this.globalButtons$.next(this.buttons?.global ?? []);
        this.globalMenuButtons$.next(this.buttons?.globalMenu ?? []);
    }

    /**
     * Установка данных для палитры
     */
    setPaletteData(data: any) {
        const paletteData: IPaletteRecord[] = typeof data === 'object' ? Object.values(data) : [];
        if (!paletteData.length) {
            return;
        }

        this.dataPalette = [];
        paletteData.forEach((palette) => {
            const hasData = !palette.merged && palette.data && palette.data.length;
            const hasMergedData = palette.merged && palette.merged_data && palette.merged_data.length;
            if (hasData || hasMergedData) {
                this.dataPalette.push(palette);
                const paletteColumn = 'system_color_' + palette.index;

                // Проверяем, существует ли уже такой столбец
                if (this.settingsTable.displayedColumns.includes(paletteColumn)) {
                    // Если столбец уже существует, пропускаем его добавление
                    return;
                }

                const paletteIndex = this.dataPalette.indexOf(palette);
                if (this.settingsTable.displayedColumns[paletteIndex] !== paletteColumn) {
                    this.settingsTable.displayedColumns.splice(paletteIndex + 1, 0, paletteColumn);
                }
            }
        });
        this.#checkDisplayPaletteIndicators();
        this.#setStatePaletteColumns(paletteData);

        /* Установка палитры, по которой будет происходить покраска */
        this.getMainPalette(this.dataPalette);
    }

    /**
     * Получение ключа для хранения данных
     * @param stringKey определяющая часть ключа
     */
    getTableKey(stringKey: string) {
        let key = this.tableService?.className + stringKey;
        if (this.dynamicFilter && this.dynamicFilter.factor_id) {
            key += this.dynamicFilter.factor_id;
        }
        if (this.dynamicFilter && this.dynamicFilter.relation_id) {
            key += this.dynamicFilter.relation_id;
        }
        return key;
    }

    /**
     * Проставление первичных данных
     */
    setFirstConfig() {
        const adminColumns = {};
        const idx = (col) => (this.dynamicFilter ? col.id + '_' + (col.relation_id ? col.relation_id : '') : col.name);
        if (this.settingsTable.adminColumns && this.settingsTable.adminColumns.length) {
            this.settingsTable.adminColumns.forEach((column) => {
                adminColumns[idx(column)] = column;
            });
        }
        this.settingsTable.userIndex = 1;
        this.settingsTable.userSize = DEFAULT_PAGE_SIZE;
        this.settingsTable.userColumns = [];
        this.settingsTable.userTree = {
            select: [],
            all: [],
        };
        this.settingsTable.allColumns.forEach((allColumn) => {
            const node = { name: allColumn.entity, level: 1, children: [] };
            allColumn.columns.forEach((column, index) => {
                node.children.push({
                    parent: allColumn.entity,
                    name: column.title,
                    alias: column.name,
                    type: column.type,
                    factor_type: column.factor_type,
                    is_sticky: false,
                    is_main: false,
                    is_fixed: column.is_fixed,
                    width: column.width,
                    columns: [],
                    level: 2,
                    transformParam: column?.transformParam,
                });
                if (this.dynamicFilter) {
                    node.children[node.children.length - 1].id = column.id;
                    if (column.relation_id) {
                        node.children[node.children.length - 1].relation_id = column.relation_id;
                    }
                }
                if (this.settingsTable.adminColumns && this.settingsTable.adminColumns.length) {
                    if (adminColumns[idx(column)]) {
                        node.children.splice(node.children.length - 1, 1);
                        adminColumns[idx(column)].entity = allColumn.entity;
                        adminColumns[idx(column)].is_main = allColumn.is_main && index == 0;
                    }
                } else if (allColumn.is_main && index < 5) {
                    node.children.splice(node.children.length - 1, 1);
                    this.addDefaultColumn(allColumn.entity, column, index == 0);
                }
            });
            this.settingsTable.userTree.all.push(Object.assign({}, node));
        });
        if (this.settingsTable.adminColumns && this.settingsTable.adminColumns.length) {
            this.settingsTable.adminColumns.forEach((column) => {
                this.addAdminColumn(adminColumns[idx(column)].entity, column);
            });
        }
        this.settingsTable.userSort = (this.settingsTable.adminSort ?? []).map((x) => ({ name: x.name, sort: x.sort }));
    }

    /**
     * Добавление колонки из админских настроек
     * @param entity сущность
     * @param column колонка
     * @param is_main колонка главной сущности
     */
    addAdminColumn(entity: string, column: InterfaceSettingsUserColumns) {
        this.settingsTable.userColumns = [...this.settingsTable.userColumns, column];
        this.settingsTable.userTree.select.push({
            parent: entity,
            id: column.id as string,
            name: column.title,
            alias: column.name,
            type: column.type,
            factor_type: column.factor_type,
            is_sticky: column.is_sticky,
            is_main: false,
            width: column.width,
            columns: [],
            level: 1,
            transformParam: column?.transformParam,
        });
        if (column.is_fixed) {
            this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].is_fixed =
                column.is_fixed;
        }
        if (typeof column.relation_id != 'undefined') {
            this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].relation_id =
                column.relation_id;
        }
        this.settingsTable.displayedColumns = [...this.settingsTable.displayedColumns, column.name];
    }

    /**
     * Добавление колонки по умолчанию
     * @param entity сущность
     * @param column колонка
     * @param is_main колонка главной сущности
     */
    addDefaultColumn(entity: string, column: InterfaceSettingsUserColumns, is_main: boolean) {
        this.settingsTable.userColumns.push({
            name: column.name,
            title: column.title,
            type: column.type,
            factor_type: column.factor_type,
            is_sticky: false,
            is_main,
            is_group: false,
            width: !!column.width ? column.width : this.defaultWidth,
            is_fixed: !!column.is_fixed ? column.is_fixed : false,
            transformParam: column?.transformParam,
        });
        if (column.type === 'boolean') {
            this.filterTable[column.name] = '';
        }
        this.settingsTable.userTree.select.push({
            id: column.id as string,
            parent: entity,
            name: column.title,
            alias: column.name,
            type: column.type,
            factor_type: column.factor_type,
            is_sticky: false,
            is_main: false,
            is_fixed: false,
            width: this.defaultWidth,
            columns: [],
            level: 1,
            transformParam: column?.transformParam,
        });
        if (this.dynamicFilter) {
            this.settingsTable.userColumns[this.settingsTable.userColumns.length - 1].id = column.id;
            this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].id = column.id as string;
            this.settingsTable.userColumns[this.settingsTable.userColumns.length - 1].name = column.name;
            this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].alias = column.name;
            if (column.relation_id) {
                this.settingsTable.userColumns[this.settingsTable.userColumns.length - 1].relation_id =
                    column.relation_id;
                this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].relation_id =
                    column.relation_id;
            }
        }
        if (this.dynamicFilter) {
            this.settingsTable.displayedColumns.push(
                this.settingsTable.userColumns[this.settingsTable.userColumns.length - 1].name,
            );
        } else {
            this.settingsTable.displayedColumns.push(column.name);
        }
    }

    /**
     * Очистка колонок
     * Удаляет столбец buttons, если он приходит из БД.
     */
    clearDisplayedColumns() {
        const index = this.settingsTable.displayedColumns.indexOf('buttons');
        if (index !== -1) {
            this.settingsTable.displayedColumns.splice(index, 1);
        }
    }

    /**
     * Проставление всех колонок
     */
    setAllColumns() {
        const selected = {};
        this.settingsTable.userTree.select.forEach((node) => {
            if (this.dynamicFilter) {
                selected[node.id + '_' + (node.relation_id ? node.relation_id : '')] = true;
                if (node.columns && node.columns.length) {
                    node.columns.forEach((child) => {
                        selected[child.id + '_' + (child.relation_id ? child.relation_id : '')] = true;
                    });
                }
            } else {
                selected[node.alias] = true;
                if (node.columns && node.columns.length) {
                    node.columns.forEach((child) => {
                        selected[child.alias] = true;
                    });
                }
            }
        });
        this.settingsTable.userTree.all = [];
        this.settingsTable.allColumns.forEach((allColumn) => {
            const node = { name: allColumn.entity, level: 1, children: [] };
            allColumn.columns.forEach((column) => {
                if (this.dynamicFilter) {
                    if (!selected[column.id + '_' + (column.relation_id ? column.relation_id : '')]) {
                        node.children.push({
                            parent: allColumn.entity,
                            name: column.title,
                            alias: column.name,
                            type: column.type,
                            factor_type: column.factor_type,
                            is_sticky: false,
                            is_main: false,
                            width: this.defaultWidth,
                            is_fixed: false,
                            columns: [],
                            level: 2,
                            transformParam: column?.transformParam,
                        });
                        node.children[node.children.length - 1].id = column.id;
                        if (column.relation_id) {
                            node.children[node.children.length - 1].relation_id = column.relation_id;
                        }
                    }
                } else {
                    if (!selected[column.name]) {
                        node.children.push({
                            parent: allColumn.entity,
                            name: column.title,
                            alias: column.name,
                            type: column.type,
                            factor_type: column.factor_type,
                            is_sticky: false,
                            is_main: false,
                            width: this.defaultWidth,
                            is_fixed: false,
                            columns: [],
                            level: 2,
                            transformParam: column?.transformParam,
                        });
                    }
                }
            });
            this.settingsTable.userTree.all.push(Object.assign({}, node));
        });
    }

    /**
     * Получение данных таблицы
     * @param isPage флаг изменения страницы
     * TODO: было бы круто этот метод вызывать снаружи и подписываться на его завершение
     */
    getData(isPage: boolean): void {
        this.requestError = '';
        if (!isPage) {
            this.settingsTable.userIndex = 1;
        }

        const data = {
            rows: this.settingsTable.userSize,
            page: this.settingsTable.userIndex,
            sort: this.getSortParam(this.settingsTable.userSort),
            group: this.settingsTable.userGroup,
            global_filter: this.globalFilter,
            custom_filter: this.customFilter,
            extended_filter: this.extendFilter,
            filter: this.settingsTable.userFilter,
            module: this.tableModule,
        };
        this.spinner.show(this.spinnerName);
        this.tableSkeleton.show();
        this.dataRequested.emit();
        this.columnActionTooltip.next({ preloader: true, text: this.appService.language_dictionary.system_loading });

        this.tableService
            .getLists(data)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    if (!this.isLoaded$.value) {
                        this.isLoaded$.next(true);
                        setTimeout(() => {
                            this.setAutoWidth();
                        }, 0);
                    }
                    this.spinner.hide(this.spinnerName);
                    this.tableSkeleton.hide();
                    this.dataReceived.emit(result?.data?.rows ?? []);
                    this.columnActionTooltip.next({
                        preloader: false,
                        text: `${this.appService.language_dictionary.search_found}: ${result?.data?.rows.length ?? 0}`,
                    });
                    setTimeout(() => {
                        this.increaseCount();
                    });

                    if (!result || result?.is_error === true) {
                        throw new Error(result?.message);
                    }

                    // текущий номер страницы не попадает в набор данных #55210
                    // запрашиваем заново первую страницу
                    if (isPage && Number(result.data?.records ?? 0) === 0 && data.page > 1) {
                        setTimeout(() => this.getData(false));
                        return;
                    }

                    this.viewDataTable(result.data);
                    setTimeout(() => {
                        this.setAutoWidth();
                    }, 0);
                    this.detectChanges();
                },
                error: (error: Error) => {
                    if (isDevMode()) {
                        console.error(error);
                    }

                    if (!this.isLoaded$.value) {
                        this.isLoaded$.next(true);
                        setTimeout(() => {
                            this.setAutoWidth();
                        }, 0);
                    }

                    this.spinner.hide(this.spinnerName);
                    this.tableSkeleton.hide();
                    this.isLoaded$.next(true);
                    this.dataReceived.emit([]);

                    this.requestError = error?.message;
                    if (isDevMode()) {
                        console.error(this.requestError);
                    }
                    setTimeout(() => {
                        this.increaseCount();
                    });
                    this.detectChanges();
                },
            });
    }

    /**
     * Отображение данных таблицы
     * @param data данные запроса
     */
    viewDataTable(data: any) {
        this.dataTables.filteredData = data.rows;

        // Отображение древовидной структуры
        if (this.groupByTreeColumns.isTree) {
            this.groupByTree();
        }

        this.updateRowNumbers();

        if (this.settingsEditFields?.isEdit) {
            this.initFormEdit();
        }
        this.emptyResult$.next(this.dataTables.filteredData.length <= 0);
        this.lengthTables = parseInt(data.records, 10);
        this.recordsFoundChange.emit(this.lengthTables);
        this.dataTables._updatePaginator(this.lengthTables);
        this.selection.clear();
        setTimeout(() => {
            if (this.dataMatTable) {
                this.dataMatTable.updateStickyColumnStyles();
            }

            const scroll = this.token.getScrollTable(this.getTableKey('ScrollTable'));
            if (scroll && document.querySelector('.mat-table-container')) {
                document.querySelector('.mat-table-container').scroll(0, scroll);
            }
        }, 0);
    }

    /**
     * Изменение пагинации
     * @param event событие пагинации
     */
    onPaginateChange(event?: PageEvent): PageEvent {
        this.settingsTable.userIndex = event.pageIndex;
        this.settingsTable.userSize = event.pageSize;
        this.saveScrollTable(true);
        if (this.tableService) {
            let settingData = Object.assign({}, this.settingsTable);
            if (this.dynamicFilter) {
                settingData = Object.assign(settingData, this.dynamicFilter);
            }
            if (this.tableModule) {
                settingData.module = this.tableModule;
            }
            this.tableService.saveTableSettings(settingData).toPromise();
        }
        this.getData(true);
        return event;
    }

    /**
     * Обработка события сортировки от попапа с настройкой сортировки
     * @param event новый набор колонок для сортировки
     */
    setSortColumns(event: ISortRun[]): void {
        if (event.length > 5) {
            this.notifier.error(this.appService.language_dictionary.t_notification_action_limit);
            return;
        }
        this.settingsTable.userSort = event.map((x) => ({
            name: x.userColumnName,
            sort: x.sortDirection,
        }));
        this.normalizeUserSort();
        this.#afterChangingSettings();
    }

    /**
     * Обработка события сортировки от заголовка колонки таблицы
     * @param column новые параметры сортировки для этой колонки
     */
    modifySortColumn(column: ISortRun) {
        const target = this.settingsTable.userSort.find((x) => x.name === column.userColumnName);
        if (!target) {
            if (this.settingsTable.userSort.length >= 5) {
                this.notifier.error(this.appService.language_dictionary.t_notification_action_limit);
                return;
            }
            this.settingsTable.userSort.push({
                name: column.userColumnName,
                sort: column.sortDirection,
            });
        } else {
            if (column.sortDirection === '') {
                this.settingsTable.userSort = this.settingsTable.userSort.filter((x) => x.name !== target.name);
                if (!this.settingsTable.userSort.length) {
                    this.resetSortColumns();
                    return;
                }
            } else {
                target.sort = column.sortDirection;
            }
        }
        this.#afterChangingSettings();
    }

    /**
     * Применить сортировку по умолчанию
     */
    resetSortColumns() {
        this.notifier.notify('success', this.appService.language_dictionary.t_notification_sort_reset);
        this.settingsTable.userSort = (this.settingsTable.adminSort ?? []).map((x) => ({ name: x.name, sort: x.sort }));
        this.normalizeUserSort();
        this.#afterChangingSettings();
    }

    /**
     * Обработка события Группировки
     * @param event событие группировки
     * @param reset Необходимость сброса группировки
     */
    groupEvent(event: IGroupRun[], reset = false): void {
        if (
            this.getCountProperty('userGroup', false) === 5 &&
            event.length > 0 &&
            !this.settingsTable.userGroup.find((groupField) => groupField.name === event[0].userColumnName)
        ) {
            // Если элементов группировки уже 5 и добавляется новый элемент
            this.notifier.error(this.appService.language_dictionary.t_notification_action_limit);
            return;
        }

        if (reset || event.length === 0 || !this.settingsTable.userGroup) {
            this.settingsTable.userGroup = [];
        }
        // Обрабатываем все элементы
        event.forEach((item: IGroupRun) => {
            let isUpdate = false;
            this.settingsTable.userGroup.forEach((groupColumn) => {
                if (groupColumn.name === item.userColumnName) {
                    groupColumn.sort = item.sortDirection;
                    isUpdate = true;
                    return;
                }
            });
            if (!isUpdate) {
                // Находим тип для каждого поля группировки
                const column = this.settingsTable.userColumns.find((elem) => elem.name === item.userColumnName);
                const fieldFactorType = column?.factor_type;
                const fieldType = column?.type;
                if (fieldFactorType || fieldType) {
                    this.settingsTable.userGroup.push({
                        name: item.userColumnName,
                        sort: item.sortDirection,
                        factor_type: fieldFactorType ?? fieldType,
                    });
                }
            }
        });

        if (this.settingsTable.userGroup.length > 0) {
            this.groupByTreeColumns.isTree = true;
            this.isGroup = true;
        } else {
            this.groupByTreeColumns.isTree = false;
            this.isGroup = false;
        }

        this.#afterChangingSettings();
    }

    /**
     * Обработка события Фильтрации
     * @param event событие фильтрации
     */
    filterEvent(event: IFilterRun[]) {
        this.settingsTable.userFilter = {};
        event.forEach((item: IFilterRun) => {
            if (item.userColumnName !== '') {
                const column: InterfaceSettingsUserColumns = this.settingsTable.userColumns.find(
                    (elem) => elem.name === item.userColumnName,
                );
                const columnType = column.type;
                let value = item.searchValue;

                if (columnType === 'float') {
                    value = value.replace(',', '.');
                }

                if (!this.settingsTable.userFilter[item.userColumnName]) {
                    this.settingsTable.userFilter[item.userColumnName] = [value];
                } else {
                    this.settingsTable.userFilter[item.userColumnName].push(value);
                }
            }
        });
        this.#afterChangingSettings(true, true);
    }

    /**
     * Действия после изменения настроек таблицы
     * @param getData Признак необходимости запроса данных.
     * @param resetPage Сбросить страницу при изменении фильтра.
     */
    #afterChangingSettings(getData: boolean = true, resetPage = false) {
        this.#checkExistActionColumn();
        if (resetPage) {
            this.settingsTable.userIndex = 1;
        }

        if (this.tableService) {
            let settingData = Object.assign({}, this.settingsTable);
            if (this.dynamicFilter) {
                settingData = Object.assign(settingData, this.dynamicFilter);
            }
            if (this.tableModule) {
                settingData.module = this.tableModule;
            }
            this.tableService.saveTableSettings(settingData).toPromise();
        }

        this.#setStatePaletteColumns(this.settingsTable.palette ?? []);
        this.#checkDisplayPaletteIndicators();

        this.saveScrollTable(true);
        if (getData) {
            const timeout = setTimeout(() => {
                this.settingsReloadTimeouts.delete(timeout);
                this.getData(!resetPage);
            }, 100);

            this.settingsReloadTimeouts.add(timeout);
        }
    }

    /**
     * Соответствует ли количество выбранных элементов общему количеству строк.
     */
    isAllSelected() {
        const selectableRows = this.dataTables.filteredData.filter((row) => this.isRowSelectable(row));
        return this.selection.selected.length === selectableRows.length;
    }

    /**
     * Выбирает все строки, если они не все выделены; в противном случае снимает выбор.
     */
    masterToggle() {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            // Фильтруем элементы, которые можно выделять
            const selectableItems = this.dataTables.filteredData.filter((item) => this.isRowSelectable(item));
            this.selection.select(...selectableItems);
        }
    }

    /** Проверка, можно ли выделить строку */
    isRowSelectable(item: any): boolean {
        return item?.type !== 'group';
    }

    /**
     * Формирует aria-label для чекбокса
     * @param row строка
     */
    checkboxLabel(row?: any): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
    }

    /**
     * Растягивание колонок таблицы по всей ширине
     */
    setAutoWidth() {
        const tableElement = document.getElementsByClassName(this.classTable);
        let table;
        if (tableElement && tableElement[0]) {
            table = tableElement[0];
        } else {
            table = document;
        }
        const tableContainer = table.querySelectorAll('.mat-table-container-table')[0] as HTMLDivElement;
        const tableDiv = table.querySelectorAll('.mat-table-container-table .mat-table')[0] as HTMLDivElement;
        if (tableContainer) {
            let width = tableContainer.offsetWidth - tableDiv.offsetWidth;
            if (tableContainer.scrollHeight !== tableContainer.offsetHeight) {
                width -= 18;
            }
            if (width < 1) {
                return;
            }
            let fixedColumnsCount = 0;
            this.settingsTable.userColumns.forEach((column) => {
                if (!column.is_fixed) {
                    fixedColumnsCount++;
                }
            });
            if (fixedColumnsCount > 0) {
                const widthAdd = width / fixedColumnsCount;

                const nameColumns = [];
                this.settingsTable.userColumns.forEach((column) => {
                    if (!column.is_fixed) {
                        column.width = parseFloat(String(column.width)) + widthAdd;
                        nameColumns.push(column.name);
                    }
                });
                this.settingsTable.userTree.select.forEach((column) => {
                    if (nameColumns.indexOf(this.getAliasColumn(column)) !== -1) {
                        column.width = parseFloat(column.width + '') + widthAdd;
                    }
                });
                this.dataMatTable.updateStickyColumnStyles();
            }
            this.#cdr.detectChanges();
        }
    }

    /**
     * Открытие диалога настроек таблицы
     */
    openSettingsDialog() {
        const dialogRef = this.dialog.open(SettingsTableDialogComponent, {
            disableClose: true,
            autoFocus: false,
            width: '80vw',
            height: '80vh',

            data: {
                title: this.appService.language_dictionary.system_settings_table_title,
                settings: this.settingsTable.userTree,
                tableService: this.tableService,
                dynamicFieldName: this.dynamicFieldName,
                dynamicSystemFields: this.dynamicSystemFields,
                dynamicFilter: this.dynamicFilter,
            },
        });
        dialogRef
            .keydownEvents()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    this.spinner.hide();
                    dialogRef.close({ save: false });
                }
            });
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.save) {
                    let isMain = false;
                    const userColumns = [];

                    this.settingsTable.displayedColumns = ['select'];
                    if (this.dataPalette && this.dataPalette.length) {
                        this.dataPalette.forEach((palette, index) => {
                            if (this.settingsTable.displayedColumns[index] !== 'system_color_' + palette.index) {
                                this.settingsTable.displayedColumns.splice(index, 0, 'system_color_' + palette.index);
                            }
                        });
                    }
                    this.clearDisplayedColumns();
                    this.settingsTable.userTree = result.settings;
                    setTimeout(() => {
                        this.settingsTable.userTree.select.forEach((node) => {
                            const name = this.getAliasColumn(node);
                            if (node.is_main) {
                                isMain = true;
                            }
                            userColumns.push({
                                name,
                                title: node.name,
                                factor_type: node.factor_type,
                                type: node.type,
                                is_main: node.is_main,
                                is_sticky: node.is_sticky,
                                width: !!node.width ? node.width : 100,
                                is_fixed: !!node.is_fixed,
                                is_group: node.columns.length !== 0,
                                transformParam: node?.transformParam,
                                columns: node.columns,
                            });
                            if (this.dynamicFilter) {
                                userColumns[userColumns.length - 1].id = node.id;
                                userColumns[userColumns.length - 1].name = name;
                                if (node.relation_id) {
                                    userColumns[userColumns.length - 1].relation_id = node.relation_id;
                                }
                            }
                            this.settingsTable.displayedColumns.push(name);
                        });
                        this.settingsTable.displayedColumns = this.#sortDisplayedColumns(
                            this.settingsTable.displayedColumns,
                        );
                        this.#checkDisplayPaletteIndicators();

                        if (!isMain) {
                            userColumns[0].is_main = true;
                        }
                        // Если в фильтре остались колонки, которых больше нет, необходимо от них избавиться
                        Object.keys(this.settingsTable?.userFilter || {}).forEach((key) => {
                            const transformKey = key.replace('|', '_');
                            if (userColumns.findIndex((row) => row?.name === transformKey) === -1) {
                                delete this.settingsTable.userFilter[key];
                            }
                        });

                        this.settingsTable.userColumns = userColumns;
                        this.normalizeUserSort();
                        this.#checkExistActionColumn();

                        if (this.tableService) {
                            let settingData = Object.assign({}, this.settingsTable);
                            if (this.dynamicFilter) {
                                settingData = Object.assign(settingData, this.dynamicFilter);
                            }
                            if (this.tableModule) {
                                settingData.module = this.tableModule;
                            }

                            this.tableService
                                .saveTableSettings(settingData)
                                .toPromise()
                                .then(() => {
                                    this.getData(true);
                                });

                            this.#setStatePaletteColumns(this.settingsTable.palette ?? []);

                            setTimeout(() => {
                                this.setAutoWidth();
                            }, 100);
                        }
                    });
                } else if (result.saveCard) {
                    this.saveCard.emit({
                        isClearTableSettings: result.isClearTableSettings,
                        dynamicFilter: this.dynamicFilter,
                        tableService: this.tableService,
                    });
                }
            });
    }

    /**
     * Проставление функций для отрисовки
     */
    setCellForColumns() {
        this.settingsTable.userColumns.forEach((sort) => {
            this.settingsTable.userTree.select.forEach((node) => {
                if (
                    !sort.is_group &&
                    this.dynamicFilter &&
                    node.id == sort.id &&
                    node.relation_id == sort.relation_id
                ) {
                    node.alias = sort.name;
                }
            });
        });
    }

    /**
     * Получение алиаса колонки
     * @param node: {TreeNode} node ветка дерева колонок
     */
    getAliasColumn(node: TreeNode): string {
        let name = '';
        if (node.columns.length == 0) {
            name = node.alias;
        } else {
            const str = [];
            node.columns.forEach((alias) => str.push(alias.alias));
            name = str.join('_');
        }
        return name;
    }

    /**
     * Построение подсказки к ячейке
     */
    getCellTooltip(column: any, row: any): string {
        const alias = !column.alias ? column.name : column.alias;
        if (row[alias] === undefined || row[alias] === null) {
            return '';
        }

        if (!column.columns || !column.columns.length || !Array.isArray(column.columns)) {
            const str = row[alias] + '';
            if (column.type === 'text') {
                return str.replace(/<\s*(div|p|h[1-6]|ul|ol|li|blockquote|br)(\s+[^>]*?)?\s*>/g, ' ');
            }

            return str.replace(/{br}/g, ', ');
        }

        return (column.columns as TreeNode[]).map((x) => this.getCellTooltip(x, row)).join(', ');
    }

    /**
     * Проверка пустоты фильтров
     */
    isEmptyFilter(): boolean {
        if (this.globalFilter) {
            return false;
        }

        if (!this.isEmptyData(this.extendFilter)) {
            return false;
        }
        if (this.settingsTable.userFilter && !this.isEmptyData(this.settingsTable.userFilter)) {
            return false;
        }

        return true;
    }

    /**
     * Проверка наличия данных в объекте фильтра
     * @param data данные фильтра
     */
    isEmptyData(data: any): boolean {
        let isEmpty = true;

        for (const val of Object.values(data)) {
            switch (typeof val) {
                case 'object':
                    if (val === null) {
                        break;
                    }
                    isEmpty = this.isEmptyData(val);
                    break;
                case 'boolean':
                case 'number':
                    isEmpty = false;
                    break;
                default:
                    isEmpty = !Boolean(val);
            }
            if (!isEmpty) {
                return isEmpty;
            }
        }
        return isEmpty;
    }

    /** Формирование данных для дерева */
    public groupByTree(): void {
        const expandedGroups = new Map(
            this.hasLocalData
                ? this.filteredDataTreeAll.filter((row) => row.type === 'group').map((row) => [row.id, row.expanded])
                : [],
        );
        this.dataTables.filteredData = this.addGroupsTree(this.dataTables.filteredData);
        this.filteredDataTreeAll = this.dataTables.filteredData.slice();

        // Применяем состояние развернутости, что сохранено у пользователя
        if (this.settingsTable.userGroupExpanded !== undefined) {
            this.toggleExpandAll(this.settingsTable.userGroupExpanded);
        }

        if (this.hasLocalData) {
            this.filteredDataTreeAll.forEach((row) => {
                if (row.type === 'group' && expandedGroups.has(row.id)) {
                    row.expanded = expandedGroups.get(row.id);
                }
            });
            this.dataTables.filteredData = this.filteredDataTreeAll.filter(
                (row) => !row.parent || this.isExpandedParents(row.parent),
            );
        }
    }

    /**
     * Генерация данных для дерева
     * @param data данные дерева
     * @param parent_id ид родительской записи
     * @param level уровень ветки в дереве
     * @return TreeNodeRegister[]
     */
    generateTreeData(data: any[], parent_id: string | number, level: number): any[] {
        const result: any[] = [];
        data.forEach((value) => {
            if (value.parent_id == parent_id) {
                const node: any = Object.assign({}, value);
                node.level = level;
                node.expanded = true;
                node.children = this.generateTreeData(data, node.id, level + 1);
                node.is_children = node.children.length != 0;
                result.push(node);
            }
        });
        return result;
    }

    /**
     * Генерация массива для дерева
     * @param data данные таблицы
     * @param parent родительская запись
     */
    generateArrayTree(data: any[], parent: any) {
        let result: any[] = [];
        data.forEach((value) => {
            const node = Object.assign({}, value);
            delete node.children;
            node.parent = parent;
            result.push(node);
            if (value.is_children) {
                result = result.concat(this.generateArrayTree(value.children, node));
            }
        });
        return result;
    }

    /**
     * Нажатие на строку
     * @param row строка
     */
    groupHeaderTreeClick(event: MouseEvent, row: any) {
        const previousRowCount = this.dataTables.filteredData.length;
        row.expanded = !row.expanded;
        const data = [];
        this.filteredDataTreeAll.forEach((value) => {
            if (!value?.parent || (value?.parent && this.isExpandedParents(value?.parent))) {
                data.push(value);
            }
        });
        this.dataTables.filteredData = data;
        this.updateRowNumbers();

        if (data.length !== previousRowCount) {
            this.rowsVisibilityChanged.emit();
        }

        event.preventDefault();
        event.stopImmediatePropagation();
    }

    /**
     * Свернуть/развернуть все элементы таблицы
     * @param expand - разворачиваем или сворачиваем
     */
    toggleExpandAll(expand: boolean) {
        const previousRowCount = this.dataTables.filteredData.length;

        // Проходим по всем элементам дерева
        this.filteredDataTreeAll.forEach((item) => {
            item.expanded = expand; // Устанавливаем состояние для всех элементов
        });

        // Обновляем отображаемые данные
        const data = [];
        this.filteredDataTreeAll.forEach((value) => {
            if (!value?.parent || (value?.parent && this.isExpandedParents(value?.parent))) {
                data.push(value);
            }
        });
        this.dataTables.filteredData = data;
        this.updateRowNumbers();

        if (data.length !== previousRowCount) {
            this.rowsVisibilityChanged.emit();
        }
    }

    /**
     * Проверка отображения строк
     * @param parent родительская запись
     */
    isExpandedParents(parent: any) {
        let expanded = parent.expanded;
        if (expanded && parent.parent) {
            expanded = this.isExpandedParents(parent.parent);
        }
        return expanded;
    }

    /**
     * Формирование данных для таблицы древовидной формы
     * @param data
     */
    addGroupsTree(data: any[]): any[] {
        const tree = this.generateTreeData(data, this.groupByTreeColumns.rootId, 1);
        return this.generateArrayTree(tree, null);
    }

    /**
     * Инициализация формы редактирование строк в таблице
     */
    initFormEdit() {
        this.formEdit = this.formBuilder.group({});
        this.dataTables.filteredData.forEach((row: any) => {
            Object.keys(this.settingsEditFields.fields).forEach((field) => {
                const formGroup = this.formBuilder.group({});
                this.formEdit.addControl(field + '_' + row?.id, formGroup);
                formGroup.addControl('value', this.formBuilder.control(row[field] ? row[field] : null, []));
                formGroup.addControl('id', this.formBuilder.control(row?.id ? row.id : null, []));
                formGroup.addControl('field', this.formBuilder.control(field, []));
            });
        });
        for (const control in this.formEdit.controls) {
            if (this.formEdit.controls.hasOwnProperty(control)) {
                this.formEdit
                    .get(control)
                    .valueChanges.pipe(debounceTime(this.appService.timeSave), takeUntil(this.destroy$))
                    .subscribe((value) => this.onFieldChange.next(value));
            }
        }
    }

    /**
     * Удаление списка записей
     * @param selected выбранные записи
     */
    removeRecords(selected: any[]) {
        const ids = [];
        selected.forEach((value) => ids.push(value.id));
        this.dialogConfirm
            .confirm(
                this.appService.language_dictionary.system_confirm_action,
                this.appService.language_dictionary.system_are_you_sure_want_delete,
            )
            .pipe(take(1))
            .subscribe((res) => {
                if (res) {
                    this.spinner.show(this.spinnerName);
                    this.tableService
                        .remove({ id: ids })
                        .toPromise()
                        .then((result: IAppRequest) => {
                            this.spinner.hide(this.spinnerName);
                            if (result && result.is_error === false) {
                                this.notifier.notify(
                                    'success',
                                    this.appService.language_dictionary.system_data_successfully_deleted,
                                );
                                this.getData(true);
                            } else {
                                this.notifier.notify('error', result.message);
                            }
                        })
                        .catch(() => {
                            this.spinner.hide(this.spinnerName);
                            this.notifier.notify(
                                'error',
                                this.appService.language_dictionary.system_no_connection_with_system,
                            );
                        });
                }
            });
    }

    /**
     * Получение сортировки для отправки в запросе
     * @param userSort текущая сортировка
     */
    private getSortParam(userSort: { name: string; sort: TCustomSortDirection }[]) {
        const sortResult = [];
        if (userSort) {
            userSort.forEach((sort) => {
                const node = this.settingsTable.userTree.select.find(
                    (column) => sort.name === this.getAliasColumn(column),
                );
                if (!node) {
                    const column = this.settingsTable.userColumns.find((column) => column.name === sort.name);
                    if (column && !column.is_group && !column.columns?.length) {
                        sortResult.push(sort);
                    }
                    return;
                }
                if (node.columns.length === 0) {
                    sortResult.push(sort);
                } else {
                    // Разбиение объединенной колонки на составляющие ее колонки
                    node.columns.forEach((column) =>
                        sortResult.push({
                            name: column.alias,
                            sort: sort.sort,
                            type: column.type,
                            factor_type: column.factor_type,
                            transformParam: column?.transformParam,
                        }),
                    );
                }
            });
        }
        return sortResult;
    }

    /**
     * Получение основной палитры
     */
    getMainPalette(palette: any) {
        for (const key in palette) {
            if (palette[key].main) {
                this.mainPalette = 'color_' + palette[key].index;
            }
        }
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEventTwo(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.#turnFullScreenOff();
        }
    }

    /** Плавное отображение хедера таблицы при наведении на панель инструментов таблицы */
    @HostListener('mouseover', ['$event'])
    onMouseEnter(event: MouseEvent) {
        if (this.isFullScreenMode && this.tablePanel?.nativeElement?.contains(event.target as Node)) {
            this.tableHeader?.nativeElement.classList.add('show');
        }
        if (this.matTable && this.matTable?.nativeElement?.contains(event.target as Node)) {
            this.tableHeader?.nativeElement.classList.remove('show');
        }
    }

    /**
     * Получить выравнивание в ячейке
     * @param column - колонка
     */
    getAlign(column): string | boolean {
        if (column && column.type) {
            switch (column.type) {
                case 'float':
                case 'int':
                case 'integer':
                    return 'right';
                default:
                    return false;
            }
        }
        return false;
    }

    /**
     * Добавить сквозную нумерацию в таблицу
     */
    getTablePagination(): number {
        if (this.settingsTable.userIndex !== 1) {
            return this.settingsTable.userSize * (this.settingsTable.userIndex - 1);
        }
        return 0;
    }

    /** Получить информацию о столбце группировки для правильного отображения данных */
    getColumnGroupInfo(row: any): InterfaceSettingsUserColumns | null {
        return (
            this.settingsTable.userColumns.find(
                (item: InterfaceSettingsUserColumns) => row.group_column_name === item.name,
            ) ?? null
        );
    }

    // Получаем записи без учета родительских узлов (группировка)
    filteredRows(): any {
        return this.dataTables.filteredData.filter(
            (row: any) => (row.type !== 'group' && this.isGroup) || !this.isGroup,
        );
    }

    /** Подготовить номера при изменении видимых строк, исключая заголовки групп. */
    private updateRowNumbers(): void {
        let number = this.getTablePagination();
        this.rowNumbers = this.dataTables.filteredData.map((row: InterfaceTableElement) =>
            !this.isGroup || row.type !== 'group' ? ++number : undefined,
        );
    }

    /**
     * Проверка того, нужно ли задизаблить кнопку
     * @param button кнопка, для которой проводится проверка
     */
    isDisabledButton(button: IButton): boolean {
        if (button.records === 'one' && this.selection.selected.length !== 1) {
            return true;
        }

        if (button.records === 'many' && this.selection.selected.length === 0) {
            return true;
        }

        if (typeof button.disabled === 'boolean') {
            return button.disabled;
        }

        if (typeof button.disabled === 'function') {
            return button.disabled(this.selection.selected);
        }

        if (typeof button.disabled === 'object') {
            return (
                button.records === 'one' &&
                this.selection.selected.length === 1 &&
                this.selection.selected[0][button?.disabled?.field] === button?.disabled?.condition
            );
        }

        return false;
    }

    /**
     * Увеличиваем счетчик построенных элементов
     * @module report_constructor
     */
    increaseCount(): void {
        if (this.tableService?.exportReady$) {
            // В момент построения отчета изменяем количество готовых элементов
            const num = this.tableService.exportReady$.value + 1;
            this.tableService.exportReady$.next(num);
        }
    }

    /**
     * Обнаружить изменения
     */
    public detectChanges() {
        this.#cdr.detectChanges();
    }

    /**
     * Определяет порядок displayedColumns в таблице.
     * Порядок - fixedOrder, systemColorColumns, все остальное
     * @param displayedColumns
     */
    #sortDisplayedColumns(displayedColumns: string[]): string[] {
        // Столбцы которые всегда есть
        const fixedOrder = TABLE_FIXED_COLUMS;
        // Столбцы палитры
        const systemColorColumns = displayedColumns.filter((col) => col.startsWith('system_color_'));

        const columns = [...fixedOrder, ...systemColorColumns];

        // Все остальные столбцы
        const unknownColumns = displayedColumns.filter((col) => !columns.includes(col));

        return [...columns, ...unknownColumns];
    }

    /**
     * Обработчик для выделения по нажатию shift
     */
    selectionKeyboardHandler() {
        let isSelectingRange = false;
        let rangeToSelect = [];

        this.selection.changed.pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (isSelectingRange) {
                return;
            }

            if (this.shiftKeyPressed$.getValue()) {
                const selectedItems = this.selection.selected;
                if (selectedItems.length > 0) {
                    const firstSelectedItem = selectedItems[0];
                    const lastSelectedItem = selectedItems[selectedItems.length - 1];

                    const filteredData = this.dataTables.filteredData;
                    let firstIndex = filteredData.findIndex((item) => item === firstSelectedItem);
                    let lastIndex = filteredData.findIndex((item) => item === lastSelectedItem);

                    if (firstIndex > lastIndex) {
                        const temp = firstIndex;
                        firstIndex = lastIndex;
                        lastIndex = temp;
                    }

                    if (firstIndex !== -1 && lastIndex !== -1) {
                        rangeToSelect = filteredData.slice(firstIndex, lastIndex + 1);
                    }
                }

                if (rangeToSelect?.length) {
                    isSelectingRange = true;
                    rangeToSelect.forEach((item) => this.selection.select(item));
                    isSelectingRange = false;
                }
            }
        });
    }

    @HostListener('window:keydown.shift', ['$event'])
    handleShiftDown(_event) {
        if (!this.shiftKeyPressed$.getValue()) {
            this.shiftKeyPressed$.next(true);
        }
    }

    @HostListener('window:keyup.shift', ['$event'])
    handleShiftUp(_event) {
        if (this.shiftKeyPressed$.getValue()) {
            this.shiftKeyPressed$.next(false);
        }
    }

    /**
     * Клик по строке таблицы
     */
    handleRowClick(event: MouseEvent, row: any) {
        if (this.settingsEditFields?.isEdit) {
            return;
        }

        if (this.isGroup && row?.type === 'group') {
            this.groupHeaderTreeClick(event, row); // Если это родительская строка
        } else {
            this.selection.toggle(row); // Обычная обработка клика
        }
    }

    /**
     * Двойной клик по строке таблицы
     */
    handleDblClick(event: MouseEvent, row: any) {
        if (row.type !== 'group') {
            event.stopPropagation();
            this.callParent('dblClickRow', row, event);
        }
    }

    /**
     * Правый клик по строке (отображение меню)
     * @param event Событие мыши
     * @param row Строка таблицы
     */
    onRightClick(event: MouseEvent, row: ITableContextMenuRow): void {
        event.preventDefault();
        if (row?.type !== 'group' || !row.type) {
            this.selection.select(row);
        }

        if (this.tableHeaderComponent) {
            this.tableHeaderComponent.openContextMenu(event);
            return;
        }

        if (!row?.id) {
            return;
        }

        this.rowContextMenu.emit(createTableRowContextMenuEvent(event, createTableContextMenuRecord(row.id)));
    }

    /**
     * Выход из полноэкранного режима
     */
    #turnFullScreenOff() {
        this.isFullScreenMode = false;
        this.appService.showSideMenu.next(true);
        this.adjustButtons();
        if (this.tableHeader) {
            this.tableHeader.nativeElement.classList.remove('show');
        }
    }

    /**
     * Открытие попапа настроек сортировки
     */
    openSortPopup() {
        this.columnActionType = EActionColumn.sort;
        this.columnActionTooltip.next(null);

        this.valuesFormColumnActions = (this.settingsTable.userSort ?? []).map(
            (item) =>
                ({
                    userColumnName: item.name,
                    sortOrderOrSearchValue: item.sort,
                }) as IValueForm,
        );

        const rect = this.sortButtonRef.nativeElement.getBoundingClientRect();
        this.tableColumnAction.show(rect.left, rect.bottom);
    }

    openGroupPopup(groupData: IGroupRun = null) {
        this.columnActionType = EActionColumn.group;
        this.columnActionTooltip.next(null);

        const userGroup: IValueForm[] =
            this.settingsTable?.userGroup?.map((item) => ({
                userColumnName: item.name,
                sortOrderOrSearchValue: item.sort,
            })) || [];

        if (groupData) {
            userGroup.push({
                userColumnName: groupData.userColumnName,
                sortOrderOrSearchValue: groupData.sortDirection,
            });
            this.#afterChangingSettings();
        }

        this.valuesFormColumnActions = Object.values(userGroup);

        const rect = this.groupButtonRef.nativeElement.getBoundingClientRect();
        this.tableColumnAction.show(rect.left, rect.bottom);
    }

    openFilterPopup(dataFilter: IFilterRun = null) {
        this.columnActionType = EActionColumn.filter;
        this.columnActionTooltip.next(null);

        const userFilter: IValueForm[] = this.settingsTable?.userFilter
            ? Object.entries(this.settingsTable.userFilter).flatMap(([name, values]) =>
                  values.map((value) => ({
                      userColumnName: name,
                      sortOrderOrSearchValue: value,
                  })),
              )
            : [];

        if (dataFilter) {
            userFilter.push({
                userColumnName: dataFilter.userColumnName,
                sortOrderOrSearchValue: dataFilter.searchValue,
            });
        }

        this.valuesFormColumnActions = Object.values(userFilter);

        let left = dataFilter?.left;
        let bottom = dataFilter?.bottom;
        if (!left) {
            const rect = this.filterButtonRef.nativeElement.getBoundingClientRect();
            left = rect.left;
            bottom = rect.bottom;
        }

        this.tableColumnAction.show(left, bottom);
    }

    handlePinToggle(pinToggleData: IPinOrUnpinRun) {
        this.settingsTable.userTree.select.forEach((item) => {
            if (item.alias === pinToggleData.userColumnName) {
                item.is_sticky = !item.is_sticky;
            }
        });

        this.settingsTable.userColumns.forEach((item) => {
            if (item.name === pinToggleData.userColumnName) {
                item.is_sticky = !item.is_sticky;
            }
        });

        this.#afterChangingSettings(false);
    }

    /** Скрыть отображаемый столбец таблицы пользователя */
    handleHideColumn(hideData: IHideRun) {
        const groupIdx = (this.settingsTable.userGroup ?? []).findIndex((x) => x.name === hideData.userColumnName);
        if (groupIdx !== -1) {
            this.settingsTable.userGroup.splice(groupIdx, 1);
        }

        this.settingsTable.userColumns = this.settingsTable.userColumns.filter(
            (item) => item.name !== hideData.userColumnName,
        );

        this.settingsTable.userTree.select = this.settingsTable.userTree.select.filter(
            (item) => item.alias !== hideData.userColumnName,
        );

        // Добавляем столбец в дерево не добавленных колонок
        this.settingsTable.allColumns.forEach((registry) => {
            const findColumn: InterfaceSettingsUserColumns = registry.columns.find(
                (column) => column.name === hideData.userColumnName,
            );
            if (findColumn) {
                const treeColumn = {
                    id: findColumn.id as string,
                    name: findColumn.title,
                    alias: findColumn.name,
                    factor_type: findColumn.factor_type,
                    parent: registry.entity,
                    type: findColumn.type,
                    is_fixed: false,
                    is_main: false,
                    is_sticky: false,
                    columns: [],
                    level: 2,
                };
                this.settingsTable.userTree.all.forEach((registry2) => {
                    if (registry2.name === registry.entity) {
                        registry2.children = [...registry2.children, treeColumn];
                    }
                });
            }
        });

        this.settingsTable.displayedColumns = this.settingsTable.displayedColumns.filter(
            (item) => item !== hideData.userColumnName,
        );

        this.#afterChangingSettings(groupIdx !== -1);
    }

    groupCustomAction(actionName: TGroupCustomAction) {
        switch (actionName) {
            case 'collapse':
                this.settingsTable.userGroupExpanded = false;
                this.toggleExpandAll(false);
                break;
            case 'expand':
                this.settingsTable.userGroupExpanded = true;
                this.toggleExpandAll(true);
                break;
        }
        // Сохраняем настройки таблицы
        this.#afterChangingSettings(false);
    }

    /** Получить длину свойства таблицы */
    getCountProperty(propertyName: string, isString = true): string | number {
        if (propertyName === 'userFilter') {
            // По одной колонке можно фильтровать несколько значений, считаем кол-во значений
            const propertyValues = this.settingsTable[propertyName];
            if (propertyValues === undefined || propertyValues === null) {
                return isString ? '' : 0;
            }

            // Суммируем длины всех массивов в объекте
            const totalCount = Object.values(propertyValues).reduce((count, array) => {
                return count + (Array.isArray(array) ? array.length : 0);
            }, 0);

            return totalCount > 0 ? (isString ? ` (${totalCount})` : totalCount) : isString ? '' : 0;
        }
        const propertyValue = this.settingsTable[propertyName];
        if (propertyValue === undefined || propertyValue === null) {
            return isString ? '' : 0;
        }
        return isString ? ' (' + Object.keys(propertyValue).length + ')' : Object.keys(propertyValue).length;
    }

    /** Признак работы с действием */
    isWorkingAction(propertyName: string): boolean {
        const propertyValue = this.settingsTable[propertyName];
        if (propertyValue === undefined || propertyValue === null) {
            return false;
        }
        return Object.keys(this.settingsTable[propertyName]).length > 0;
    }

    /**
     * Если мы убрали поле (через настройки таблицы),
     * по которому выполняется сортировка по умолчанию,
     * то переключать сортировку на первое по списку поле.
     * #67465
     */
    normalizeUserSort() {
        if (!this.settingsTable.userColumns?.length) {
            return;
        }
        const filtered = (this.settingsTable.userSort ?? []).filter(
            (userSortColumn) => this.settingsTable.userColumns.findIndex((x) => x.name === userSortColumn.name) !== -1,
        );
        if (filtered.length) {
            return;
        }
        const firstUserColumn = this.settingsTable.userColumns[0];
        this.settingsTable.userSort = [
            {
                name: firstUserColumn.name,
                sort: 'asc',
                type: firstUserColumn.type,
                factor_type: firstUserColumn.factor_type,
            },
        ];
    }

    /** Получить данные о колонке для меню действий над колонкой */
    getDataColumnByMenu(column: InterfaceSettingsUserColumns): IDataColumnMenu {
        const columnSort = (this.settingsTable?.userSort ?? []).find((item) => item.name === column.name);
        const columnGroup = (this.settingsTable?.userGroup ?? []).find((item) => item.name === column.name);
        return {
            title: column.title,
            name: column.name,
            is_sticky: column.is_sticky,
            groupDirection: columnGroup?.sort ?? '',
            sortDirection: columnSort?.sort ?? '',
            factor_type: column?.factor_type ?? 'string',
        };
    }

    /** Колонки палитры - установка состояний столбцов палитры, по которым можно выполнять действия */
    #setStatePaletteColumns(paletteData: IPaletteRecord[]) {
        const columnsDisplayMenu: Record<string, IDataColumnMenu> = {};
        paletteData.forEach((palette) => {
            let columnName = '';
            switch (palette.alias_prompt) {
                case 'factor_statuses':
                    columnName = 'card_status_name';
                    break;
                case 'is_map':
                    columnName = 'has_geo';
                    break;
                default:
                    return;
            }
            const userColumn: InterfaceSettingsUserColumns = this.settingsTable.userColumns?.find(
                (item) => item.name === columnName,
            );

            const userSort = this.settingsTable?.userSort?.find((item) => item.name === columnName);
            const userGroup = this.settingsTable?.userGroup?.find((item) => item.name === columnName);
            columnsDisplayMenu[palette.title] = {
                title: palette.title,
                name: userColumn?.name ?? '',
                is_sticky: userColumn?.is_sticky ?? false,
                groupDirection: userGroup?.sort ?? '',
                sortDirection: userSort?.sort ?? '',
                factor_type: userColumn?.factor_type ?? 'string',
            };
        });
        this.showMenuColumnPalette$.next(columnsDisplayMenu);
    }

    /** Проверяет должны ли выводиться индикаторы палитры в таблицу, убирает их если не должны */
    #checkDisplayPaletteIndicators() {
        // Если колонки "Наличие геопривязки" нет - не отображаем индикатор геопривязки
        if (!this.settingsTable.displayedColumns.includes('has_geo')) {
            this.settingsTable.displayedColumns = this.settingsTable.displayedColumns.filter(
                (item) => item !== 'system_color_1',
            );
        } else if (!this.settingsTable.displayedColumns.includes('system_color_1')) {
            this.settingsTable.displayedColumns.push('system_color_1');
        }

        // Если колонки "Статус записи" нет - не отображаем индикатор
        if (!this.settingsTable.displayedColumns.includes('card_status_name')) {
            this.settingsTable.displayedColumns = this.settingsTable.displayedColumns.filter(
                (item) => item !== 'system_color_2',
            );
        } else if (!this.settingsTable.displayedColumns.includes('system_color_2')) {
            this.settingsTable.displayedColumns.push('system_color_2');
        }
    }

    /** Получить колонки, которые должны отображаться в таблице */
    getDisplayedUserColumn() {
        // Столбец наличия геопривязки не выводим, для него есть индикатор
        // Столбец статуса записи не выводим, для него есть индикатор
        return this.settingsTable.userColumns.filter(
            (item) => item.name !== 'has_geo' && item.name !== 'card_status_name',
        );
    }

    /** Получить колонки, которые должны отображаться в таблице */
    getDisplayedColumn() {
        // Столбец наличия геопривязки не выводим, для него есть индикатор
        // Столбец статуса записи не выводим, для него есть индикатор
        return this.settingsTable.displayedColumns.filter((item) => item !== 'has_geo' && item !== 'card_status_name');
    }

    /**
     * Проверка существования колонок, по которым выполняются действия
     * (удаляет колонку из действия если она не существует)
     */
    #checkExistActionColumn() {
        // Создаем копию объекта, чтобы не мутировать оригинал
        const cleanedSettings = { ...this.settingsTable };

        // Собираем все имена столбцов из userColumns в Set для быстрого поиска
        const validColumnNames = new Set<string>();

        if (cleanedSettings.userColumns) {
            cleanedSettings.userColumns.forEach((column) => {
                validColumnNames.add(column.name);
            });
        }

        // Проверка userSort
        if (cleanedSettings.userSort) {
            cleanedSettings.userSort = cleanedSettings.userSort.filter((sortItem) =>
                validColumnNames.has(sortItem.name),
            );
        }

        // Проверка userGroup
        if (cleanedSettings.userGroup) {
            cleanedSettings.userGroup = cleanedSettings.userGroup.filter((groupItem) =>
                validColumnNames.has(groupItem.name),
            );

            if (cleanedSettings.userGroup.length === 0) {
                this.groupByTreeColumns.isTree = false;
                this.isGroup = false;
            }
        }

        // Проверка userFilter
        if (cleanedSettings.userFilter && Object.keys(cleanedSettings.userFilter).length > 0) {
            const filteredUserFilter: Record<string, string[]> = {};

            Object.keys(cleanedSettings.userFilter).forEach((key) => {
                if (validColumnNames.has(key)) {
                    filteredUserFilter[key] = cleanedSettings.userFilter[key];
                }
            });

            cleanedSettings.userFilter = filteredUserFilter;
        }

        this.settingsTable = cleanedSettings;
    }
}
